import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { api } from "../lib/api";

const IMG = "https://static.prod-images.emergentagent.com/jobs/047283dd-777d-49aa-87e9-fa629b5cc4bd/images/4464525c265e112c7fec1c6320b87f7314614d3e5651d5ddaf2c28be7e721c22.jpeg";
const KEY = "hh_newsletter_popup";
const SNOOZE_DAYS = 30;
const DELAY_MS = 6000;
const SKIP = ["/admin", "/checkout", "/order-confirm", "/payment"];

export function NewsletterPopup() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (SKIP.some((p) => pathname.startsWith(p))) return;
    const saved = JSON.parse(localStorage.getItem(KEY) || "null");
    if (saved?.subscribed || (saved?.dismissed_at && Date.now() - saved.dismissed_at < SNOOZE_DAYS * 864e5)) return;
    const t = setTimeout(() => setOpen(true), DELAY_MS);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dismiss = () => {
    localStorage.setItem(KEY, JSON.stringify({ ...(result ? { subscribed: true } : {}), dismissed_at: Date.now() }));
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/newsletter", { email, marketing_consent: true, source: "popup" });
      setResult(data);
      localStorage.setItem(KEY, JSON.stringify({ subscribed: true, dismissed_at: Date.now() }));
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong — please try again.");
    } finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-6" role="dialog" aria-modal="true" aria-labelledby="newsletter-popup-title" data-testid="newsletter-popup">
          <motion.div className="absolute inset-0 bg-forest/50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={dismiss} />
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.5, ease: [0.3, 1, 0.3, 1] }}
            className="relative w-full sm:max-w-3xl bg-white rounded-t-2xl sm:rounded-2xl overflow-hidden grid sm:grid-cols-2 shadow-2xl">
            <img src={IMG} alt="" aria-hidden="true" width="600" height="600" className="hidden sm:block w-full h-full object-cover min-h-[420px]" />
            <div className="p-7 sm:p-10 flex flex-col justify-center text-center sm:text-left">
              <button onClick={dismiss} aria-label="Close" data-testid="newsletter-popup-close" className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white border border-line flex items-center justify-center hover:bg-surface"><X className="w-4 h-4" /></button>
              {result ? (
                <div data-testid="newsletter-popup-success">
                  <p className="eyebrow">You're In</p>
                  <h2 id="newsletter-popup-title" className="font-display text-3xl sm:text-4xl mt-2">Welcome to the hive</h2>
                  <p className="text-sm text-subtext mt-3">{result.message}</p>
                  {result.code && (
                    <div className="mt-5 border border-dashed border-charcoal rounded-xl bg-surface py-4 text-center font-body text-xl tracking-[0.08em]" data-testid="newsletter-popup-code">{result.code}</div>
                  )}
                  <p className="text-xs text-subtext mt-3">10% off your first order (up to ₹300). One use per code — we've also emailed it to you.</p>
                  <button onClick={dismiss} className="btn-primary mt-6 w-full sm:w-auto">Start Shopping</button>
                </div>
              ) : (
                <form onSubmit={submit}>
                  <p className="eyebrow">Join The Newsletter</p>
                  <h2 id="newsletter-popup-title" className="font-display text-3xl sm:text-[2.6rem] leading-tight mt-2">Sign up to get 10% OFF</h2>
                  <p className="text-sm text-subtext mt-3">Your one-time welcome code lands in your inbox straight away — plus first looks at new drops and little surprises.</p>
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" aria-label="Email address"
                    data-testid="newsletter-popup-email" className="field mt-6" />
                  {error && <p role="alert" className="text-xs text-coral mt-2" data-testid="newsletter-popup-error">{error}</p>}
                  <button type="submit" disabled={busy} data-testid="newsletter-popup-submit" className="btn-primary w-full mt-3 disabled:opacity-60">{busy ? "Sending…" : "Get My 10% Code"}</button>
                  <button type="button" onClick={dismiss} data-testid="newsletter-popup-dismiss" className="block mx-auto sm:mx-0 text-xs text-subtext underline underline-offset-4 mt-4 hover:text-charcoal">No thanks, I'll pay full price</button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
